import React, { useState, useEffect, useCallback } from "react";
import { Modal, Select, Text, Alert } from "@mantine/core";
import { PlanogramViewer } from "@labeling-tools/ui-components/src/planogram-viewer/planogram-viewer.comp";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
  mvpApiGetPlanogramProductOptions,
  mvpApiListPlanogramsOptions,
} from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { useAppDispatch } from "@/store/hooks";
import { useAtlasStores } from "@labeling-tools/api/hooks/atlas/useAtlasStores";
import { useAtlasProduct } from "@labeling-tools/api/hooks";
import {
  setPlanogramSelectedProductId,
  setStagedSelectedItem,
} from "@/store/index";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import { PlanogramProduct } from "@labeling-tools/ui-components/src/PlanogramProduct";
import { PAPlanogramModalActions } from "./PAPlanogramModalActions";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";

export const PlanogramModal = ({
  storeId,
  capturedImage,
  task,
  planogramSelectedProductId,
}: {
  storeId: string;
  capturedImage: React.ReactNode;
  task: JobTaskDetailSchema;
  planogramSelectedProductId: string | null;
}) => {
  const dispatch = useAppDispatch();
  const [opened, setOpened] = useState(false);
  const [planogramIdOverride, setPlanogramIdOverride] = useState<string | null>(
    null,
  );
  const { data: atlasStores, isLoading: atlasStoresLoading } =
    useAtlasStores(storeId);

  // zustand state vars
  // the planogram-viewer component uses zustand to store the active label box info
  const activePlanogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.product_id,
  );
  const planogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.planogram_product_id,
  );
  const resetActivePlanoProductInfoStore = useActivePlanoProductInfoStore(
    (s) => s.reset,
  );

  // query planograms
  // first we try to find store-specific planograms
  // if none are found, we fall back to global planograms (atlasProduct.attached_planograms)
  const { data, isLoading: planogramsQueryLoading } = useQuery({
    ...mvpApiListPlanogramsOptions({
      query: {
        store_id: atlasStores?.stores[0]?.id,
        product_id: planogramSelectedProductId,
      },
    }),
    enabled: !!atlasStores?.stores[0]?.id && !!planogramSelectedProductId,
  });
  const planogramsLoading = planogramsQueryLoading || atlasStoresLoading;

  // atlas product - used to find associated planogram(s)
  const { data: atlasProduct } = useAtlasProduct(planogramSelectedProductId);

  // decipher which planograms to use
  const planogramId =
    data?.planograms[0]?.id || atlasProduct?.attached_planograms?.[0]?.id;
  const usingAttachedPlanograms =
    !!atlasProduct?.attached_planograms?.length &&
    !data?.planograms?.length &&
    !planogramsLoading;
  const allPlanograms = data?.planograms?.length
    ? data?.planograms
    : atlasProduct?.attached_planograms;
  const planograms = allPlanograms?.filter(
    (planogram, index, self) =>
      self.findIndex((p) => p.id === planogram.id) === index,
  );

  // planogram product - used for displaying the product details
  const { data: selectedProduct, isLoading: selectedProductLoading } = useQuery(
    {
      ...mvpApiGetPlanogramProductOptions({
        path: {
          planogram_id: planogramIdOverride || planogramId || "",
          planogram_product_id: planogramProductId || "",
        },
      }),
      enabled:
        (!!planogramIdOverride || !!planogramId) && !!activePlanogramProductId,
    },
  );

  const close = useCallback(() => {
    dispatch(setPlanogramSelectedProductId(null));
    dispatch(setStagedSelectedItem(null));

    setOpened(false);
    setPlanogramIdOverride(null);
    resetActivePlanoProductInfoStore();
  }, [
    dispatch,
    setPlanogramSelectedProductId,
    setStagedSelectedItem,
    resetActivePlanoProductInfoStore,
  ]);

  // open the modal when the planogram selected product id is set
  // this happens when the "p" icon is clicked in the search results row
  useEffect(() => {
    if (planogramSelectedProductId) {
      setOpened(true);
    }
  }, [planogramSelectedProductId]);

  const handlePlanogramOverride = (value: string | null) => {
    setPlanogramIdOverride(value);
  };

  return (
    <Modal
      opened={opened}
      size="100%"
      onClose={close}
      title="Planogram Viewer"
      withinPortal={false}
      data-planogram-modal
    >
      {usingAttachedPlanograms && (
        <Alert
          variant="light"
          color="yellow"
          title=""
          icon={<IconAlertCircle />}
          style={{ marginBottom: 10, padding: 10, fontWeight: 500 }}
        >
          This planogram is not associated with this store.
        </Alert>
      )}
      <div className="mb-4">
        <Select
          value={planogramIdOverride || planogramId}
          onChange={handlePlanogramOverride}
          data={planograms?.map((planogram) => ({
            value: planogram.id || "",
            label: planogram.description || "",
          }))}
          size="md"
          w={400}
        />
      </div>
      <div className="flex">
        <div className="w-1/3 mr-4">{capturedImage}</div>
        <div className="w-2/3 relative">
          {planogramId && !planogramsLoading && (
            <PlanogramViewer
              planogram_id={planogramIdOverride || planogramId}
              initialProductId={planogramSelectedProductId}
              hidePopover={true}
            />
          )}
          {!planogramId && (
            <div>
              <Text>No planogram found</Text>
            </div>
          )}

          {/* Product Details */}
          <PlanogramProduct
            selectedProduct={selectedProduct}
            loading={selectedProductLoading}
          />

          {/* actions */}
          {!!activePlanogramProductId &&
            task?.type === "ASSOCIATION_LABELING_v2" && (
              <PAPlanogramModalActions
                task={task}
                onClose={close}
                selectedProductId={activePlanogramProductId}
                planogramId={planogramId}
              />
            )}
        </div>
      </div>
    </Modal>
  );
};
